import React from 'react';
import cn from 'classnames';
import { Helmet } from 'react-helmet-async';

import { main as mainApi } from 'client/redux/api/main';
import { useAnalytics, useTranslation } from 'client/hooks';
import { useAppSelector } from 'client/hooks/redux';
import { selectUserGAStatus, selectUserRole } from 'client/redux/user/selectors';
import { CONFIG } from 'constants/index';
import { ROOT } from 'client/constants/routes';
import { CompaniesSlider, AddYourCompanyBtnLink } from 'client/components/common';
import { selectLang } from 'client/redux/settings/selectors';

import TopSlider from './TopSlider';
import HeroSlider from './HeroSlider';
import BottomSliders from './BottomSliders';
// import JoinSection from './JoinSection';
import InvestNow from './InvestNow';
import FakeMandateSlider from './FakeMandatesSlides';

import css from './Main.module.scss';

interface Props {
  className?: string;
}

const Main: React.FC<Props> = (props) => {
  const { className = '' } = props;
  const userGAStatus = useAppSelector(selectUserGAStatus);
  const userRole = useAppSelector(selectUserRole);
  const lang = useAppSelector(selectLang);
  const analytics = useAnalytics();
  const { translate } = useTranslation();

  const { data, isLoading } = mainApi.endpoints.getMainPageSliders.useQuery({});
  const slidersData = data && data.success ? data.data : undefined;
  const { data: fakeSlides, isLoading: isLoadingFakeSlides } = mainApi.endpoints.getFakeSlides.useQuery({});
  const fakeSliderData = fakeSlides && fakeSlides.success ? fakeSlides.data : undefined;

  const { data: fakeComingSoonSlides, isLoading: isLoadingFakeComingSoonSlides } =
    mainApi.endpoints.getFCSSlides.useQuery(undefined, { skip: !slidersData?.isComingSoonVisible });
  const fakeComingSoonSliderData =
    fakeComingSoonSlides && fakeComingSoonSlides.success ? fakeComingSoonSlides.data : undefined;

  React.useEffect(() => {
    if (userGAStatus) {
      analytics.gtag.event('page_view', {
        page_title: 'MainPage',
        page_location: `${CONFIG.domainUrl}${ROOT}`,
      });
    }
  }, [userGAStatus]);

  const renderHelmet = () => {
    const ldJsonData = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Organization',
          name: 'Catalyst Investors’ Club',
          url: 'https://catalyst-ic.com',
          logo: 'https://catalyst-ic.com/static/assets/cic-logo-v2.c04aa6ab667496521ab2.svg',
          contactPoint: [
            {
              '@type': 'ContactPoint',
              telephone: '+972 3 695 0666',
              contactType: 'Customer Support',
              availableLanguage: ['English', 'Hebrew'],
            },
            {
              '@type': 'ContactPoint',
              telephone: '+972 58 785 6689',
              contactType: 'Customer Support',
              availableLanguage: ['English', 'Hebrew'],
            },
          ],
        },
        {
          '@type': 'WebSite',
          url: 'https://catalyst-ic.com',
          name: 'Catalyst Investors’ Club',
          potentialAction: {
            '@type': 'SearchAction',
            target: 'https://catalyst-ic.com/search-results?search={search_term_string}',
            'query-input': 'required name=search_term_string',
          },
          inLanguage: lang,
        },
      ],
    });

    return (
      <Helmet>
        <script type="application/ld+json">{ldJsonData}</script>
      </Helmet>
    );
  };

  return (
    <div className={cn(css.main, className)}>
      {renderHelmet()}
      <HeroSlider items={slidersData?.heroSlider} isLoading={isLoading} />
      <TopSlider items={slidersData?.topWatchedSlider} isLoading={isLoading} />
      {/* <JoinSection /> */}

      {userRole !== 'VIEWER' && slidersData?.investNowSlider && slidersData?.investNowSlider.length > 0 && (
        <InvestNow
          items={slidersData?.investNowSlider}
          isLoading={isLoading}
          title={translate('mainPageDesktop.slidersTitles.investNow')}
        />
      )}

      {userRole === 'VIEWER' && fakeSliderData && fakeSliderData.length > 0 && (
        <FakeMandateSlider
          items={fakeSliderData}
          isLoading={isLoadingFakeSlides}
          title={translate('mainPageDesktop.slidersTitles.investNow')}
        />
      )}

      <section className={css.featuredSlider}>
        <CompaniesSlider
          title={translate('mainPageDesktop.slidersTitles.featuredInnovation')}
          companies={slidersData?.featuredSlider}
          isLoading={isLoading}
        />
      </section>

      {/** Cooming Soon Sliders Start */}
      {userRole !== 'VIEWER' &&
        slidersData?.isComingSoonVisible &&
        slidersData?.comingSoonSlider &&
        slidersData?.comingSoonSlider.length > 0 && (
          <InvestNow
            items={slidersData?.comingSoonSlider}
            isLoading={isLoading}
            title={translate('mainPageDesktop.slidersTitles.comingSoon')}
          />
        )}
      {userRole === 'VIEWER' && fakeComingSoonSliderData && fakeComingSoonSliderData.length > 0 && (
        <FakeMandateSlider
          items={fakeComingSoonSliderData}
          isLoading={isLoadingFakeComingSoonSlides}
          title={translate('mainPageDesktop.slidersTitles.comingSoon')}
        />
      )}
      {/** Cooming Soon Sliders End */}

      <BottomSliders />
      <AddYourCompanyBtnLink />
    </div>
  );
};

export default Main;
